import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/jsx-fragments */
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { isEmpty } from '@knapsack/utils';
import { mergeRefs } from 'react-merge-refs';
import { useStateFromProp } from '@knapsack/hooks';
import { Backdrop } from '../backdrop/index.js';
import { TextBody, TextHeading } from '../text/index.js';
import { ActionBar } from '../action-bar/index.js';
import { TabBar } from '../tabs/index.js';
import { panelBody, panelContent, panelFooter, panelHeader, panel, panelTabbedContent, panelTheme, } from './panel.css.js';
import { useFocusTrap } from '../../utils/focus-trap.js';
export const Panel = forwardRef(({ actionBarLeft, actionBarRight, ariaLabel, children, footer, hideActionBar = false, id, onToggle, opened = false, overlay = false, placement = 'left', size = 'default', subtitle, title, tabs, testId, theme, initialOpened = false, }, ref) => {
    const [topOffset, setTopOffset] = useState(null);
    const fallbackCloseAction = (actionSlot) => {
        let closeAction = [];
        // Default close action goes in right slot
        if (actionSlot === 'left' && placement === 'right') {
            closeAction = [
                {
                    label: 'Close Panel',
                    icon: 'toggle-right',
                    onTrigger: onToggle,
                },
            ];
        }
        // Default close action goes in left slot
        if (actionSlot === 'right' && placement === 'left') {
            closeAction = [
                {
                    label: 'Close Panel',
                    icon: 'toggle-left',
                    onTrigger: onToggle,
                },
            ];
        }
        return closeAction;
    };
    const internalRef = useRef(null);
    const backdropRef = useRef(null);
    const _ref = useMemo(() => mergeRefs([internalRef, backdropRef, ref]), [internalRef, backdropRef, ref]);
    /**
     * STATE MANAGEMENT
     * - The `opened` props triggers the opening/closing animations
     * - Delay matches the css animation in modal.css.ts
     */
    const [initialAnimationDone, setInitialAnimationDone] = useState(false);
    const [isPanelOpen] = useStateFromProp(opened);
    const [panelState, setPanelState] = useState(initialOpened ? 'opened' : 'closed');
    useEffect(() => {
        if (initialAnimationDone) {
            if (isPanelOpen) {
                setPanelState('opening');
            }
            else {
                setPanelState('closing');
            }
        }
        setInitialAnimationDone(true);
    }, [initialAnimationDone, isPanelOpen]);
    useEffect(() => {
        const delay = 250;
        const timeoutId = setTimeout(() => {
            if (panelState === 'opening') {
                setPanelState('opened');
            }
            if (panelState === 'closing') {
                setPanelState('closed');
            }
        }, delay);
        return () => clearTimeout(timeoutId);
    }, [panelState]);
    // control setting the top offset internally since the theme prop may
    // change before the panel has finished animating
    useEffect(() => {
        if (!theme?.top)
            return;
        if (panelState === 'opening') {
            setTopOffset(theme.top);
        }
        if (panelState === 'closed') {
            setTopOffset(null);
        }
    }, [panelState, theme?.top]);
    useFocusTrap({
        trapElement: internalRef.current,
        active: overlay && panelState === 'opened',
    });
    return (_jsxs("aside", { id: id, className: panel({ state: panelState, overlay, placement, size }), "data-testid": testId, "aria-label": ariaLabel, ref: _ref, style: theme &&
            assignInlineVars(panelTheme, {
                backgroundColor: theme?.backgroundColor || 'white',
                position: theme?.position,
                height: theme?.height,
                top: topOffset,
            }), inert: panelState === 'closed' ? '' : undefined, children: [_jsxs("div", { className: panelContent({
                    overlay,
                    placement,
                    visible: panelState !== 'closed',
                }), children: [!hideActionBar && (_jsx(ActionBar, { leftActions: actionBarLeft || fallbackCloseAction('left'), rightActions: actionBarRight || fallbackCloseAction('right') })), _jsxs("header", { className: panelHeader({
                            hasActionBar: !hideActionBar,
                        }), "data-testid": `${testId || 'panel'}--header`, children: [title && React.isValidElement(title) && title, title && typeof title === 'string' && (_jsx(TextHeading, { tag: "h2", size: "xsmall", spacing: "none", children: title })), subtitle && (_jsx(TextBody, { size: "medium", color: "subtle", testId: `${testId || 'panel'}--subtitle`, children: subtitle }))] }), _jsxs("div", { className: panelBody({ hasTabBar: !isEmpty(tabs) }), "data-testid": `${testId || 'panel'}--body`, 
                        /**
                         * tabIndex required because the panelBody is scrollable
                         * @see https://dequeuniversity.com/rules/axe/3.5/scrollable-region-focusable
                         */
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        tabIndex: tabs ? -1 : 0, children: [_jsx(React.Suspense, { children: children }), tabs && (_jsx("div", { className: panelTabbedContent, children: _jsx(TabBar, { sticky: true, tabs: tabs }) }))] }), footer && (_jsx("footer", { className: panelFooter, "data-testid": `${testId || 'panel'}--footer`, children: footer }))] }), _jsx(Backdrop, { ref: backdropRef, elevation: 4, onTrigger: onToggle, visible: overlay && opened, canClose: true })] }));
});
